import { ChangeEventHandler, KeyboardEventHandler, useRef, useState } from 'react'
import { Transition } from 'react-transition-group'
import { i18n } from 'builder/utils/i18n'
import {
  convertToExternal,
  convertToFieldValue,
  guessMonth,
  guessYear,
  isDateHidden,
  isMonthHidden,
} from 'builder/components/DateRangePicker/utils'
import PickerPanel from 'builder/components/DateRangePicker/PickerPanel'
import { TextField } from 'builder/components/TextField'
import { Container, Label, LabelContent } from 'builder/components/DateRangePicker/styles'
import { useEffectOnMount } from 'builder/hooks/useEffectOnMount'
import { useClickOutside } from 'builder/hooks/useClickOutside'
import { DateRangePickerChangeValue } from 'builder/components/DateRangePicker/types'

type Props = {
  value: string

  onChange?: (value: DateRangePickerChangeValue) => void
  label?: string
  dateFromHighlightId?: string
  dateUntilHighlightId?: string
  isUntilToday?: boolean
}

export const SingleDatePicker = (props: Props) => {
  const { value, onChange = () => {}, label } = props
  const [startValue, setStartValue] = useState<string | null>(null)
  const [startMonth, setStartMonth] = useState<number | null>(null)
  const [startYear, setStartYear] = useState(0)
  const [isStartPickerOpen, setIsStartPickerOpen] = useState(false)
  const container = useRef<HTMLDivElement>(null)
  const transitionRef = useRef(null)

  useEffectOnMount(() => {
    const date = value ? new Date(value) : new Date()
    const monthInShort = date.toLocaleString('default', { month: 'short' })
    const fullYear = date.getFullYear()
    const startValue = `${monthInShort}, ${fullYear}`
    const startMonth = date.getMonth() + 1
    const startYear = date.getFullYear()

    setStartValue(startValue)
    setStartMonth(startMonth)
    setStartYear(startYear)
    setIsStartPickerOpen(false)
  })

  useClickOutside(container, () => {
    setIsStartPickerOpen(false)
  })

  const handleEnter = () => {}

  const handleEntering = () => {}

  const handleExiting = () => {}

  const openStartPicker = () => {
    setIsStartPickerOpen(true)
  }

  const closeStartPicker = () => {
    setIsStartPickerOpen(false)
  }

  const changeStartYear = (startYear: number) => {
    const startValue = convertToFieldValue(startMonth, startYear)

    setStartYear(startYear)
    setStartValue(startValue)

    onChange({
      dateFrom: convertToExternal(startMonth, startYear, {
        isDateHidden: isDateHidden(startValue),
      }),
    })
  }

  const updateStartPicker: ChangeEventHandler<HTMLInputElement> = e => {
    const newValue = e.target.value
    const newState = {
      startValue: newValue,
      startMonth: guessMonth(newValue),
      startYear: guessYear(newValue),
    }
    setStartValue(newState.startValue)
    setStartMonth(newState.startMonth)
    setStartYear(newState.startYear)

    onChange({
      dateFrom: convertToExternal(newState.startMonth, newState.startYear, {
        isDateHidden: isDateHidden(newState.startValue),
      }),
      isMonthFromHidden: isMonthHidden(newState.startValue),
    })
  }

  const handleStartChoose = (month: number | null, year: number) => {
    setStartMonth(month)
    setStartValue(convertToFieldValue(month, year))
    setIsStartPickerOpen(false)

    onChange({
      dateFrom: convertToExternal(month, year),
      isMonthFromHidden: !month,
    })
  }

  const handleStartBlur = () => {
    setStartValue(convertToFieldValue(startMonth, startYear, { prevValue: startValue }))

    onChange({
      dateFrom: convertToExternal(startMonth, startYear, {
        isDateHidden: isDateHidden(startValue),
      }),
      isMonthFromHidden: isMonthHidden(startValue),
    })
  }

  const handleTab = () => {
    if (isStartPickerOpen) closeStartPicker()
  }

  const handleKeyDown: KeyboardEventHandler = e => {
    let keyCode = e.keyCode

    // tab
    if (keyCode === 9) {
      handleTab()
    }
  }

  return (
    <Container onKeyDown={handleKeyDown} ref={container}>
      <Label>
        <LabelContent>{label}</LabelContent>
      </Label>
      <TextField
        name="startDate"
        onFocus={openStartPicker}
        onBlur={handleStartBlur}
        onChange={updateStartPicker}
        value={startValue || ''}
        active={isStartPickerOpen}
        placeholder={i18n.t('builder.date_range_picker.placeholder')}
        autoComplete="off"
      />

      <Transition
        in={isStartPickerOpen}
        timeout={0}
        onEnter={handleEnter}
        onEntering={handleEntering}
        onExiting={handleExiting}
        unmountOnExit
        appear
        nodeRef={transitionRef}
      >
        <PickerPanel
          month={startMonth}
          year={startYear}
          onChangeYear={changeStartYear}
          onChoose={handleStartChoose}
          noActiveMonth={isDateHidden(startValue) || isMonthHidden(startValue)}
          containerRef={transitionRef}
        />
      </Transition>
    </Container>
  )
}
